import {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { IS_PRODUCTION } from '../../../../../../../../constants/envs.ts';
import { useModalsContext } from '../../../../../../../../providers/ModalsProvider/Modals.context.ts';
import { eChannelType } from '../../../../../../../../providers/SocketsProvider/Sockets.enums.ts';
import { useWorkspaceContext } from '../../../../../../../../providers/WorkspaceProvider/Workspace.context.ts';
import { getRecordedAudioMimetype } from '../../../../../../../../utils/GetRecordedAudioMimetype.ts';
import { MediaType } from '../../../../../../Chat.enum.ts';
import { ChatService } from '../../../../../../Chat.service.ts';
import { tAttachment, tChatAttachment } from '../../../../../../Chat.types.ts';
import { useMessageBarContext } from '../../../../MessageBar.hook.ts';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from 'src/lib/logging';

export const useAudioRecorderAction = () => {
  const [audioContext, setAudioContext] = useState<AudioContext | null>(null);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(
    null,
  );
  const [recording, setRecording] = useState<boolean>(false);
  const [sendAudio, setSendAudio] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [showRecorder, setShowRecorder] = useState<boolean>(false);
  const [seconds, setSeconds] = useState<number>(0);
  const [audioIsEnabled, setAudioIsEnabled] = useState<boolean>(false);
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null);
  // BUG-921: the wall-clock start of the recording, used to label the
  // outgoing attachment with its real length.
  //
  // A ref, not the `seconds` state: recorder.onstop is registered when
  // recording STARTS, so its closure captures seconds as 0 and would report
  // every recording as zero-length. The wall clock is also more accurate than
  // the 1s interval, which browsers throttle in a backgrounded tab.
  const recordingStartedAtRef = useRef<number | null>(null);
  const { currentWorkspaceId } = useWorkspaceContext();
  const { chatAttachments, setChatAttachments } = useMessageBarContext();
  const { selectedLeadForChatModal } = useModalsContext();
  const extension =
    selectedLeadForChatModal?.chat?.integration === eChannelType.WHATSAPP_WEB
      ? 'ogg'
      : 'aac';
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (recording) {
      interval = setInterval(() => setSeconds((prev) => prev + 1), 1000);
    } else if (!recording && interval) {
      clearInterval(interval);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [recording]);

  const formatTime = (totalSeconds: number) => {
    const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
    const secs = String(totalSeconds % 60).padStart(2, '0');
    return `${minutes}:${secs}`;
  };

  const onMicrophoneClick = async (
    setNewAttachments?: Dispatch<SetStateAction<tAttachment[] | undefined>>,
  ) => {
    if (sendAudio) {
      setShowRecorder(false);
      setSeconds(0);
      setSendAudio(false);
      if (mediaRecorder) {
        mediaRecorder.stop();
        setMediaRecorder(null);
        if (audioStream) {
          audioStream.getTracks().forEach((track) => track.stop());
          setAudioStream(null);
        }
      }
      return;
    }

    setRecording(true);
    setShowRecorder(true);
    setSendAudio(false);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setAudioStream(stream);

      const context = new AudioContext();
      setAudioContext(context);

      // Record audio/mp4 and WebM with Opus for compatibility.
      const mimeType = isSafari ? 'audio/mp4' : 'audio/webm;codecs=opus';
      const recorder = new MediaRecorder(stream, { mimeType });

      const chunks: Blob[] = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.push(event.data);
      };

      recorder.onstop = async () => {
        const audioBlob = new Blob(chunks, { type: mimeType });

        // BUG-921: WhatsApp renders the duration we send verbatim and never
        // probes the file, so sending 0 is what made every voice note display
        // "0:00". Rounded up to at least 1s — a sub-second recording is still
        // audible, and 0 reads downstream as "unknown".
        const startedAt = recordingStartedAtRef.current;
        const recordedSeconds = startedAt
          ? Math.max(1, Math.round((Date.now() - startedAt) / 1000))
          : 0;
        recordingStartedAtRef.current = null;

        setLoading(true);

        const audioFile = new File([audioBlob], 'recorder_audio', {
          type: `audio/${extension}`,
        });

        await ChatService.sendAudioS3({
          workspaceId: currentWorkspaceId ?? '',
          file: audioFile,
          integration: selectedLeadForChatModal?.chat?.integration,
        })
          .then((response) => {
            const storageDomain = 'leadsales-storage.s3.amazonaws.com';
            const productionDomain = 'leadsales.services';
            const stagingDomain = 'staging.leadsales.services';

            const targetDomain = IS_PRODUCTION
              ? productionDomain
              : stagingDomain;

            const newURLResponse =
              response.data?.url?.replace(storageDomain, targetDomain) || '';

            // storage-management decides the output format (.ogg, or .aac for
            // WHATSAPP_API behind WAPI_AUDIO_AAC_ENABLED — INT-1011); the
            // label must follow its URL, not a channel table kept in sync by
            // hand. This also fixes MESSENGER, mislabeled audio/aac over .ogg.
            const uploadedMimetype = getRecordedAudioMimetype(newURLResponse);
            const uploadedFile = new File([audioBlob], 'recorder_audio', {
              type: uploadedMimetype,
            });

            const audioAttachmentPreview: tAttachment = {
              filename: 'recorder_audio',
              mediaType: MediaType.AUDIO,
              mimeType: uploadedMimetype,
              url: newURLResponse,
              vcard: null,
              decryption: null,
              duration: recordedSeconds,
              latitude: null,
              longitude: null,
            };

            if (setNewAttachments) {
              setNewAttachments((prevValues) =>
                prevValues
                  ? [...prevValues, audioAttachmentPreview]
                  : [audioAttachmentPreview],
              );
            }
            const chatAttachment: tChatAttachment = {
              type: uploadedMimetype,
              src: newURLResponse,
              name: 'recorder_audio',
              size: uploadedFile.size,
              poster: '',
              // sendFileMessage labels the outgoing attachment with file.type,
              // so the corrected mimetype has to travel on the File itself.
              file: uploadedFile,
              duration: recordedSeconds,
            };
            setChatAttachments([...chatAttachments, chatAttachment]);
          })
          .finally(() => setLoading(false));
      };

      recordingStartedAtRef.current = Date.now();
      recorder.start();
      setMediaRecorder(recorder);
      setRecording(true);
      setShowRecorder(true);
      setAudioIsEnabled(true);
    } catch (error) {
      LLog.error(
        {
          component: LOG_COMPONENTS.CHAT,
          method: 'AudioRecorderAction.hooks',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error en grabación de audio',
        error as unknown as Error,
      );
      setRecording(false);
    }
  };

  function onStopAudio() {
    setSendAudio(true);
    setRecording(false);
    try {
      if (mediaRecorder?.state === 'recording') {
        mediaRecorder.pause();
      }
    } catch (error) {
      LLog.error(
        {
          component: LOG_COMPONENTS.CHAT,
          method: 'AudioRecorderAction.hooks',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error pausing media recorder',
        error as unknown as Error,
      );
    }
  }

  function onDeleteAudio() {
    setShowRecorder(false);
    setSeconds(0);
    setSendAudio(false);
    setRecording(false);
    setMediaRecorder(null);
    audioContext?.close();
  }

  return {
    onMicrophoneClick,
    formatTime,
    seconds,
    showRecorder,
    recording,
    audioIsEnabled,
    setAudioIsEnabled,
    loading,
    onDeleteAudio,
    onStopAudio,
  };
};
